import { fetchWithToken } from "@/helpers/fetch";
import { PAGE_SIZE } from "@/modules/shared/constants/table";

// Histórico de pagos de un cliente (POST paymentHistory/search).
// Antes generaba también el PDF y el Excel del estado de cuenta, que esta vista no usa: con una
// búsqueda corta el PDF agotaba la memoria del servidor (500 sin CORS) y el loading quedaba eterno.
export const search = async ({ commit }, { payload, type }) => {
  commit(type, { isLoading: true, error: "" });
  try {
    const response = await fetchWithToken("/paymentHistory/search", payload, "POST", false);
    if (!response.ok) throw new Error("HTTP " + response.status);
    const { score, ...data } = await response.json();
    // Siempre se reemplaza: null es "Sin información" y no debe quedar el score del cliente anterior.
    commit('historyScore', score === null || score === undefined ? null : parseInt(score));
    commit(type, { data: Array.isArray(data) ? {} : { ...data }, isLoading: false });
  } catch (error) {
    console.error(error);
    commit(type, {
      data: {},
      isLoading: false,
      error: "No se pudo consultar el histórico. Revisa tu conexión e inténtalo de nuevo.",
    });
  }
};

// Buscador de clientes del histórico (ventas y admin): RUC y nombre para elegir.
export const searchPaymentHistoryClients = async (_, { q, company }) => {
  try {
    const response = await fetchWithToken("/paymentHistory/clients", { q, company }, "POST");
    const data = await response.json();
    if (response.status !== 200 || !data || !Array.isArray(data.data)) {
      const message = data && data.message ? data.message : (data && data.errors ? Object.values(data.errors).flat().join(" ") : "");
      return { success: false, clients: [], message };
    }
    return { success: true, clients: data.data };
  } catch (error) {
    console.error(error);
    return { success: false, clients: [], message: "" };
  }
};

export const getDocuments = async ({ commit }, { payload }) => { };

// Listado de facturas al contado (POST billing/cash/list, paginado).
export const searchCashBilling = async ({ commit }, { payload, page = 1 }) => {
  commit("cashBilling", { isLoading: true });
  try {
    const response = await fetchWithToken(
      "/billing/cash/list?page=" + page + "&limit=" + PAGE_SIZE,
      payload,
      "POST"
    );
    const data = await response.json();
    if (response.status !== 200 || !data || !("data" in data)) {
      commit("cashBilling", { data: { data: [] }, isLoading: false, searched: true });
      return { success: false, message: data && data.message ? data.message : "" };
    }
    commit("cashBilling", { data: { ...data }, isLoading: false, searched: true });
    return { success: true };
  } catch (error) {
    console.error(error);
    commit("cashBilling", { data: { data: [] }, isLoading: false, searched: true });
    return { success: false, message: "" };
  }
};

// Envío del comprobante al cliente (POST billing/cash/{id}/send).
export const sendCashDocument = async ({ commit }, { id, channel, recipient, cc = "", subject = "", message = "" }) => {
  commit("cashBillingSend", { isSending: true });
  try {
    const body = { channel, recipient };
    if (channel === "email") {
      if (cc) body.cc = cc;
      if (subject) body.subject = subject;
      if (message) body.message = message;
    }
    const response = await fetchWithToken("/billing/cash/" + id + "/send", body, "POST");
    const data = await response.json();
    if (response.status !== 200) {
      return { success: false, message: data && data.message ? data.message : "" };
    }
    return { success: true, ...data };
  } catch (error) {
    console.error(error);
    return { success: false, message: "" };
  } finally {
    commit("cashBillingSend", { isSending: false });
  }
};

// Asunto, mensaje y adjuntos predeterminados del correo (GET billing/cash/{id}/email-preview).
export const getCashEmailPreview = async (_, { id }) => {
  try {
    const response = await fetchWithToken("/billing/cash/" + id + "/email-preview");
    if (response.status !== 200) return null;
    const data = await response.json();
    return data && data.status === "success" ? data : null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

// Correo completo como lo recibirá el cliente (POST billing/cash/{id}/email-render).
export const renderCashEmail = async (_, { id, subject = "", message = "" }) => {
  try {
    const response = await fetchWithToken("/billing/cash/" + id + "/email-render", { subject, message }, "POST");
    const data = await response.json();
    if (response.status !== 200) return { success: false, message: data && data.message ? data.message : "" };
    return { success: true, html: data.html, subject: data.subject };
  } catch (error) {
    console.error(error);
    return { success: false, message: "" };
  }
};

// Directorio de contactos de contado.
export const searchContacts = async ({ commit }, { search = "", page = 1 }) => {
  commit("cashContacts", { isLoading: true });
  try {
    const query = "?page=" + page + "&limit=" + PAGE_SIZE + (search ? "&search=" + encodeURIComponent(search) : "");
    const response = await fetchWithToken("/contacts" + query);
    const data = await response.json();
    if (response.status !== 200 || !data || !("data" in data)) {
      commit("cashContacts", { data: { data: [] }, isLoading: false });
      return { success: false, message: data && data.message ? data.message : "" };
    }
    commit("cashContacts", { data: { ...data }, isLoading: false });
    return { success: true };
  } catch (error) {
    console.error(error);
    commit("cashContacts", { data: { data: [] }, isLoading: false });
    return { success: false, message: "" };
  }
};

export const getContactByRuc = async (_, { ruc }) => {
  try {
    const response = await fetchWithToken("/contacts/" + encodeURIComponent(ruc));
    if (response.status !== 200) return null;
    const data = await response.json();
    return data && data.contact ? data.contact : null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const saveContact = async ({ commit }, { isNew, ruc, name, email, cellphone }) => {
  commit("cashContacts", { isSaving: true });
  try {
    const response = isNew
      ? await fetchWithToken("/contacts", { ruc, name, email, cellphone }, "POST")
      : await fetchWithToken("/contacts/" + encodeURIComponent(ruc), { name, email, cellphone }, "PUT");
    const data = await response.json();
    if (response.status !== 200) {
      const message = data && data.message ? data.message : (data && data.errors ? Object.values(data.errors).flat().join(" ") : "");
      return { success: false, message };
    }
    return { success: true, message: data.message };
  } catch (error) {
    console.error(error);
    return { success: false, message: "" };
  } finally {
    commit("cashContacts", { isSaving: false });
  }
};

export const deleteContact = async (_, { ruc }) => {
  try {
    const response = await fetchWithToken("/contacts/" + encodeURIComponent(ruc), {}, "DELETE");
    const data = await response.json();
    return { success: response.status === 200, message: data && data.message ? data.message : "" };
  } catch (error) {
    console.error(error);
    return { success: false, message: "" };
  }
};

// Exportación a Excel con los mismos filtros del listado.
export const exportCashBillingExcel = async ({ commit }, { payload }) => {
  commit("cashBilling", { isExporting: true });
  try {
    const response = await fetchWithToken("/billing/cash/excel", payload, "POST");
    if (response.status !== 200) return { success: false };
    const blob = await response.blob();
    return { success: true, fileUrl: URL.createObjectURL(blob) };
  } catch (error) {
    console.error(error);
    return { success: false };
  } finally {
    commit("cashBilling", { isExporting: false });
  }
};

// Buscador de clientes crédito (POST clients/credit/search): devuelve [{ ruc, name }].
export const searchCreditClients = async (_, { q, company }) => {
  try {
    const response = await fetchWithToken("/clients/credit/search", { q, company }, "POST");
    const data = await response.json();
    if (response.status !== 200 || !data || !Array.isArray(data.data)) {
      const message = data && data.message ? data.message : (data && data.errors ? Object.values(data.errors).flat().join(" ") : "");
      return { success: false, clients: [], message };
    }
    return { success: true, clients: data.data };
  } catch (error) {
    console.error(error);
    return { success: false, clients: [], message: "" };
  }
};
